/**
 * ============================================================================
 * HomeConnectModalFormNew — Controller (client)
 * ============================================================================
 * Owns form state (react-hook-form), validation via explicit resolver,
 * SINGLE handleFormSubmit path (not V2's handleLeadCheck / handleLeadSubmit
 * split), lead-check debounce, OTP orchestration, GA/WebEngage side effects.
 * No JSX — view consumes the returned ViewModel.
 * ============================================================================
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch, type Control } from "react-hook-form";
import {
  LEAD_CHECK_SKIP_OTP,
  setLeadFormDegreeValue,
  setLeadFormProgramValue,
  setLeadFormInputValue,
} from "@/helpers/leadFormDegreeProgramFields";
import { useLeadFormValidationReset } from "@/helpers/useLeadFormValidationReset";
import { usePathname } from "next/navigation";
import { trackingDetails } from "@/api/getOtherFormDetails";
import { useOTPContext } from "@/components/sections/CommonModals/context";
import { DEGREE_TYPES } from "@/components/molecules/ProgramMenu/form-constants";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { ALL_COUNTRIES } from "@/context/countries";
import { scheduleAfterPaint } from "@/helpers/scheduleAfterPaint";
import { useIpStackDetails } from "@/helpers/useIpStackDetails";
import { fetchIpInfoData, getIpJSONData } from "@/context/ip.storage.utils";
import {
  checkExistingLead,
  createLead,
} from "../service/homeConnectModalFormNew.service";
import {
  SELECT_DEGREE,
  SELECT_PROGRAM,
  filterGlobalProgramsByDegree,
  filterSelectedPrograms,
  getProgramId,
  precedentSelectedPrograms
} from "../utils";
import {
  NATIONAL_CODE,
  createHomeConnectModalFormNewResolver,
  degreeOptions,
  errorAccordingToFieldName,
  regexForInputs,
  type HomeConnectModalFormNewFormValues,
  type HomeConnectModalFormNewProps,
} from "../model/homeConnectModalFormNew.types";

const LEAD_CHECK_DEBOUNCE_MS = 2000;
const IP_INFO_DELAY_MS = 1000;

export interface HomeConnectModalFormNewViewModel {
  // react-hook-form bindings — views bind fields via <Controller control={control} />.
  control: Control<HomeConnectModalFormNewFormValues>;
  errors: Partial<
    Record<keyof HomeConnectModalFormNewFormValues, string | undefined>
  >;
  isSubmitting: boolean;
  isOTPLoading: boolean;
  programsList: string[];
  showProgramField: boolean;
  isProgram: boolean;
  isProgramListPage: boolean;
  isMobile: boolean;
  isSpecificPage?: boolean;
  isAccaPage: boolean;
  isFaq?: boolean;
  isSeatsFilled?: boolean;
  seatsFilled?: number;
  landingPageValue: string;
  showStatsCards?: boolean;
  countries: typeof ALL_COUNTRIES;
  isLambdaAPI?: boolean;
  handleFieldChange: (
    fieldName: keyof HomeConnectModalFormNewFormValues,
    value: string | boolean,
  ) => void;
  handleDegreeChange: (value: string) => void;
  handleProgramChange: (value: string) => void;
  handleFieldBlur: (fieldName: string, value: string, error: string) => void;
  updateCountryCode: (value: string) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
  errorAccordingToFieldName: typeof errorAccordingToFieldName;
  degreeOptions: typeof degreeOptions;
  SELECT_DEGREE: string;
  SELECT_PROGRAM: string;
}

function extractDegree(program: string) {
  const match = program?.match(/(PG|UG)_Program/);
  return match ? match[1] : null;
}

export function useHomeConnectModalFormNewController(
  props: HomeConnectModalFormNewProps,
): HomeConnectModalFormNewViewModel {
  const {

    setMessage = () => { },
    setFormValuesData = () => { },
    associatedPrograms = [],
    isNewLeadForm = true,
    checkLeadFail = () => { },
    isProgram = false,
    isProgramListPage = false,
    isMobile = false,
    landingPageValue = "",
    isLandingPage,
    analyticsOverrides,
    signInFlowMode = "newTemplate",
    forceLeadResponseIdToOne = false,
    useGlobalProgramDropdown = false,
    portal,
    custom5Value = "False",
    isLambdaAPI = false
  } = props;

  const path = usePathname();
  const isAfricaPage = path == "/africa";
  const isAccaPage = path === "/lp-nw/acca";
  const { ipStackDetails, countryCode } = useIpStackDetails(isAfricaPage);
  const {
    handleSendOTP,
    isOTPLoading,
    setLeadResponse,
    isHeaderHidden,
    setIsHeaderHidden,
    setShowLoader,
    setIsNewTemplete,
    setIsNewSignInFlow,
    isOTPSent,
  } = useOTPContext();

  const analyticsSectionName = () =>
    analyticsOverrides?.sectionName ?? getGlobalStateValue("parentSection");
  const analyticsCtaText =
    analyticsOverrides?.ctaText ?? "Get Free Counselling";
  const analyticsFormFilledPageType =
    analyticsOverrides?.pageType ?? "Home Page";
  const analyticsLeadSuccessPageType =
    analyticsOverrides?.pageType ?? "Home Page";
  const analyticsLeadFailPageType = () =>
    analyticsOverrides?.pageType ?? getGlobalStateValue("pageType");

  const specializationKey = useRef<string>("");
  const custom10PageTypeKey = useRef<string>("");
  const isFiredOnceIpinfo = useRef(false);
  const apiResponseURL = useRef<string | null>(null);
  const contactIdRef = useRef<string | null>(null);

  const [showProgramField, setShowProgramField] = useState(false);
  const [isSubmitUIBusy, setIsSubmitUIBusy] = useState(false);

  // New-lead first screen: only base fields. Once programs reveal (or
  // isNewLeadForm is false), degree/program are required too.
  const requireProgramFields = showProgramField || !isNewLeadForm;

  const resolver = useMemo(
    () => createHomeConnectModalFormNewResolver({ requireProgramFields }),
    [requireProgramFields],
  );

  const defaultFormValues: HomeConnectModalFormNewFormValues = {
    fullName: "",
    countryCode: NATIONAL_CODE,
    phoneNumber: "",
    emailId: "",
    degree: landingPageValue || SELECT_DEGREE,
    program: SELECT_PROGRAM,
    isTermsAccepted: true,
  };

  const {
    control,
    setValue,
    getValues,
    trigger,
    clearErrors,
    reset,
    handleSubmit: rhfHandleSubmit,
    formState: { errors: rhfErrors, isSubmitting },
  } = useForm<HomeConnectModalFormNewFormValues>({
    defaultValues: defaultFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver,
  });

  useLeadFormValidationReset({
    clearErrors,
    isOTPSent,
    showProgramField: showProgramField,
    isHeaderHidden,
  });

  // Narrow useWatch subscriptions for side-effect effects only.
  // Field values in the view come from per-field <Controller> render props.
  const watchedPhoneNumber = useWatch({ control, name: "phoneNumber" });
  const watchedCountryCode = useWatch({ control, name: "countryCode" });
  const watchedEmailId = useWatch({ control, name: "emailId" });
  const watchedFullName = useWatch({ control, name: "fullName" });
  const watchedDegree = useWatch({ control, name: "degree" });

  const isLoading = isSubmitUIBusy || isSubmitting;

  useEffect(() => {
    if (signInFlowMode === "newTemplate") {
      setIsNewTemplete(true);
    } else if (signInFlowMode === "newSignInFlow") {
      setIsNewSignInFlow(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setValue("countryCode", countryCode);
  }, [ipStackDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      (watchedFullName || watchedEmailId || watchedPhoneNumber) &&
      !isFiredOnceIpinfo.current
    ) {
      setTimeout(fetchIpInfoData, IP_INFO_DELAY_MS);
      isFiredOnceIpinfo.current = true;
    }
  }, [watchedFullName, watchedEmailId, watchedPhoneNumber]);

  // Re-validate phone when country code changes (match pre-MVC).
  useEffect(() => {
    if (getValues("phoneNumber")) {
      trigger("phoneNumber");
    }
  }, [watchedCountryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const leadCheckOnCompletion = useCallback(async () => {
    const currentValues = getValues();
    await checkExistingLead({
      phoneNumber: currentValues.phoneNumber,
      emailId: currentValues.emailId,
      fullName: currentValues.fullName,
      countryCode: currentValues.countryCode,
    });
  }, [getValues]);

  // Debounced phone lead-check (2s).
  useEffect(() => {
    let isValid = false;

    if (watchedCountryCode === "+91") {
      isValid = regexForInputs.phoneNumberIndia.test(watchedPhoneNumber);
    } else {
      isValid = regexForInputs.phoneNumberIntl.test(watchedPhoneNumber);
    }

    if (!isValid) return undefined;

    const handler = setTimeout(() => {
      leadCheckOnCompletion();
    }, LEAD_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [watchedPhoneNumber, watchedCountryCode, leadCheckOnCompletion]);

  // Debounced email lead-check (2s).
  useEffect(() => {
    let isValid = false;

    if (regexForInputs.emailId.test(watchedEmailId)) {
      isValid = true;
    }

    if (!isValid) return undefined;

    const handler = setTimeout(() => {
      leadCheckOnCompletion();
    }, LEAD_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [watchedEmailId, leadCheckOnCompletion]);

  const programsList = useMemo(() => {
    if (useGlobalProgramDropdown) {
      return filterGlobalProgramsByDegree({
        degree: watchedDegree,
        countryCode: ipStackDetails?.country_code,
      });
    }
    return precedentSelectedPrograms(
      filterSelectedPrograms({
        associatedPrograms,
        degree: watchedDegree,
      }),
    );
  }, [
    watchedDegree,
    ipStackDetails?.country_code,
    associatedPrograms,
    useGlobalProgramDropdown,
  ]);

  const updateCountryCode = useCallback(
    (value: string) => {
      setValue("countryCode", value, { shouldValidate: true, shouldDirty: true });
      setValue("phoneNumber", "", { shouldDirty: true });
      clearErrors("phoneNumber");
    },
    [setValue, clearErrors],
  );

  const handleFieldBlur = useCallback(
    (fieldName: string, value: string, error: string) => {
      // Same event/payload — after paint so blur/focus paints first (INP).
      scheduleAfterPaint(() => {
        pushToDataLayer({
          event: "form_filled",
          field_name: fieldName,
          option_selected: value || "NA",
          error_message: error || "NA",
          section_name: analyticsSectionName(),
          page_type: analyticsFormFilledPageType,
          page_url: window?.location?.href || "NA",
        });
      });
    },
    [analyticsFormFilledPageType],
  );

  const handleFieldChange = useCallback(
    (
      fieldName: keyof HomeConnectModalFormNewFormValues,
      value: string | boolean,
    ) => {
      setLeadFormInputValue(setValue, trigger, fieldName, value as any);
    },
    [setValue, trigger],
  );

  const handleDegreeChange = useCallback(
    (value: string) => {
      setLeadFormDegreeValue(setValue, clearErrors, value, SELECT_PROGRAM);
      const errMsg = degreeOptions.includes(value)
        ? ""
        : errorAccordingToFieldName.degree;
      handleFieldBlur("degree", value, errMsg);
    },
    [setValue, clearErrors, handleFieldBlur],
  );

  const handleProgramChange = useCallback(
    (value: string) => {
      setLeadFormProgramValue(setValue, clearErrors, value, SELECT_PROGRAM);
      handleFieldBlur(
        "program",
        value,
        value !== SELECT_PROGRAM ? "" : errorAccordingToFieldName.program,
      );

      const selectedProgram = associatedPrograms.find(
        (obj) =>
          obj.attributes.BasicDetails?.Heading?.toLocaleUpperCase() === value,
      );

      if (selectedProgram) {
        specializationKey.current =
          selectedProgram.attributes.BasicDetails?.SpecializationText || "";
        custom10PageTypeKey.current =
          selectedProgram.attributes.BasicDetails?.Custom10PgType || "";
      }
    },
    [associatedPrograms, setValue, clearErrors, handleFieldBlur],
  );

  /**
   * Single Home submit path — always Custom5 "False", degree/Custom6/Custom10,
   * lead-create-new, and ID==='1' only reveals programs when
   * associatedPrograms.length !== 1.
   */
  const submitLead = useCallback(
    async (payload: HomeConnectModalFormNewFormValues) => {
      setFormValuesData(payload);

      const form: Record<string, any> = {
        name: payload.fullName,
        phone: payload.phoneNumber,
        countryCode: payload.countryCode,
        email: payload.emailId,
        Custom6: specializationKey.current,
        program: payload.program,
        PROGRAM_ID: getProgramId({
          associatedPrograms,
          program: payload.program,
          isLandingPage,
        }),
        degree: DEGREE_TYPES[payload.degree]?.name,
        consent: true,
        ContactId: contactIdRef.current || "",
      };
      if (portal !== undefined) {
        form.Portal = portal;
      }
      const tracking = trackingDetails();

      const submitCb = () => {
        setIsHeaderHidden(true);
      };

      const verifyCb = () => {
        setShowLoader(false);
        props?.setAPIResposeURL?.(apiResponseURL.current as string);
        props?.setApplicationRedirectLoaderDisabled?.(false);
        reset({
          fullName: "",
          countryCode: "+" + ipStackDetails?.location?.calling_code || "91",
          phoneNumber: "",
          emailId: "",
          degree: landingPageValue || SELECT_DEGREE,
          program: SELECT_PROGRAM,
          isTermsAccepted: true,
        });
        props?.isOtpValidated?.(true);
      };

      const cb = () =>
        new Promise((resolve, reject) => {
          // connect-counsellor fires automatically from inside createLead
          // (leadCreateFetch) — see src/utils/connectCounsellor.ts.

          async function performApiCalls() {
            let leadSquareResponse: any;
            try {
              let response: any;
              if (isLambdaAPI) {
                response = await createLead({
                  form,
                  tracking,
                  ipstack: getIpJSONData(),
                  endpoint: "lead-create-lp-new",
                  formExtra: {
                    Custom5: "False",
                    Custom10: custom10PageTypeKey.current || "",
                  }
                });
              } else {
                response = await createLead({
                  form,
                  tracking,
                  ipstack: getIpJSONData(),
                  endpoint: "lead-create-new",
                  formExtra: {
                    Custom5: custom5Value,
                    Custom10: custom10PageTypeKey.current || "",
                  },
                });
              }

              if (typeof response === "string") {
                leadSquareResponse = JSON.parse(response as unknown as string);
              } else {
                leadSquareResponse = await response.json();
              }

              if (leadSquareResponse?.success) {
                const resp = leadSquareResponse?.response || {};
                apiResponseURL.current = leadSquareResponse.response.URL || "";

                if (resp?.ContactId) {
                  contactIdRef.current = resp.ContactId;
                }

                // Classic HomeConnectModalForm: set leadResponse before the
                // ID==='1' early-return so OTP step-2 still has a lead payload.
                if (
                  forceLeadResponseIdToOne &&
                  typeof setLeadResponse === "function"
                ) {
                  setLeadResponse(resp);
                }

                // Home-specific: ID==='1' only reveals programs when more than one
                // associated program (single-program pages skip the step-2 reveal).
                if (resp.ID === "1" && associatedPrograms.length !== 1) {
                  props.isOtpValidated?.(false);
                  setShowProgramField(true);
                  setIsSubmitUIBusy(false);
                  // Settle lead-check so await handleSendOTP / RHF isSubmitting
                  // clear without sending OTP before Degree/Program are filled.
                  resolve(LEAD_CHECK_SKIP_OTP);
                  return;
                }

                if (props.setFormResponse) {
                  props.setFormResponse({
                    ID: resp?.ID,
                    link: resp?.URL,
                    ProspectId: resp?.ProspectId,
                  });
                }

                pushToDataLayer({
                  event: "lead_submit",
                  cta_text: analyticsCtaText,
                  lead_starting_cta:
                    getGlobalStateValue("leadStartingCta") || "Apply Now",
                  lead_starting_url: window.location.href || "NA",
                  lead_id: resp?.ProspectId || "NA",
                  section_name: analyticsSectionName(),
                  status: "success",
                  error_message: "NA",
                  page_type: analyticsLeadSuccessPageType,
                  lead_type:
                    resp.ID === "1" ? "lead_submit_1" : "lead_submit_2",
                  page_url: window?.location?.href || "NA",
                });

                // Match OpenRfi: keep GTM sync; defer WebEngage off the
                // response turn so OTP/UI transitions are not blocked (same events).
                setTimeout(() => {
                  window?.webengage?.user?.login(resp?.ContactId);

                  window?.webengage?.user?.setAttribute(
                    "we_email",
                    payload.emailId,
                  );
                  window?.webengage?.user?.setAttribute(
                    "we_first_name",
                    payload.fullName,
                  );
                  window?.webengage?.user?.setAttribute(
                    "we_phone",
                    payload.countryCode + payload.phoneNumber,
                  );

                  window?.webengage?.track("Lead Form Filled", {
                    "Student Nationality":
                      payload.countryCode === NATIONAL_CODE
                        ? "National"
                        : "International",
                    "First Name": payload.fullName,
                    "Phone Number": payload.countryCode + payload.phoneNumber,
                    "Email ID": payload.emailId,
                    "Form Type": "Apply",
                    "Section Name": getGlobalStateValue("parentSection"),
                    "Application URL": resp?.URL || "",
                  });
                }, 0);

                if (typeof setLeadResponse === "function") {
                  setLeadResponse(
                    forceLeadResponseIdToOne || showProgramField
                      ? { ...resp, ID: "1" }
                      : resp,
                  );
                }
                setIsHeaderHidden(true);
                setMessage(
                  `Your request for counselling has been sent ${leadSquareResponse?.isDuplicate ? "already" : ""
                  }, and our counsellor will reach out to you in the next few hours.`,
                );

                if (payload?.countryCode !== NATIONAL_CODE) {
                  setIsSubmitUIBusy(false);
                  verifyCb();
                  props?.isOtpValidated?.(true, resp?.ProspectId);
                }
                // Keep busy for +91 — OTP send / isOTPLoading takes over.
              } else {
                setShowLoader(false);
                setShowProgramField(true);
                setIsSubmitUIBusy(false);
                resolve(LEAD_CHECK_SKIP_OTP);
                return;
              }
              resolve(leadSquareResponse?.response);
            } catch (error) {
              pushToDataLayer({
                event: "lead_submit",
                cta_text: analyticsCtaText,
                lead_starting_cta:
                  getGlobalStateValue("leadStartingCta") || "Apply Now",
                lead_starting_url: window.location.href || "NA",
                lead_id: "NA",
                section_name: analyticsSectionName(),
                status: "fail",
                error_message: error || "NA",
                page_type: analyticsLeadFailPageType(),
                lead_type: "lead_submit_1",
                page_url: window?.location?.href || "NA",
              });
              console.error("LeadSquare API call failed:", error);
              setShowProgramField(true);
              setMessage(error);
              setIsSubmitUIBusy(false);
              reject(error);
              return;
            }
          }

          performApiCalls();
        });

      // Await so paint-first busy stays through lead + OTP handoff (OpenRfi).
      await handleSendOTP(form, cb, submitCb, verifyCb, checkLeadFail);
    },
    [
      analyticsCtaText,
      analyticsLeadSuccessPageType,
      associatedPrograms,
      checkLeadFail,
      custom5Value,
      forceLeadResponseIdToOne,
      handleSendOTP,
      ipStackDetails,
      isLandingPage,
      landingPageValue,
      portal,
      props,
      reset,
      setFormValuesData,
      setIsHeaderHidden,
      setLeadResponse,
      setMessage,
      setShowLoader,
      showProgramField,
    ],
  );

  const handleFormSubmit = useCallback(
    async (e: React.FormEvent) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (isLoading || isOTPLoading) return;

      try {
        if (associatedPrograms.length === 1) {
          const programDegree =
            associatedPrograms[0].attributes.BasicDetails.ProgramType;
          const programTitle =
            associatedPrograms[0].attributes.BasicDetails.Heading;
          specializationKey.current =
            associatedPrograms[0].attributes.BasicDetails?.SpecializationText ||
            "";
          custom10PageTypeKey.current =
            associatedPrograms[0].attributes.BasicDetails?.Custom10PgType ||
            "";
          const derivedDegree = extractDegree(programDegree);
          setValue("degree", derivedDegree as string);
          setValue("program", programTitle);
          await import("@/helpers/safeSubmit").then((m) =>
            m.submitAfterValidation({
              rhfHandleSubmit,
              startBusy: () => setIsSubmitUIBusy(true),
              onValid: async () => {
                await submitLead({
                  ...getValues(),
                  degree: derivedDegree as string,
                  program: programTitle,
                });
              },
              onInvalid: () => setIsSubmitUIBusy(false),
              event: e,
            }),
          );

          return;
        }

        await import("@/helpers/safeSubmit").then((m) =>
          m.submitAfterValidation({
            rhfHandleSubmit,
            startBusy: () => setIsSubmitUIBusy(true),
            onValid: async (values) => {
              await submitLead(values);
            },
            onInvalid: () => setIsSubmitUIBusy(false),
            event: e,
          }),
        );
      } catch {
        setIsSubmitUIBusy(false);
      }
    },
    [
      associatedPrograms,
      getValues,
      isLoading,
      isOTPLoading,
      rhfHandleSubmit,
      setValue,
      submitLead,
    ],
  );

  // Mirrors the resolver so the submit button stays disabled until the
  // currently VISIBLE fields are valid. Degree/program are only required
  // once the second step of the lead flow reveals them.

  const errors = {
    fullName: rhfErrors.fullName?.message,
    countryCode: rhfErrors.countryCode?.message,
    phoneNumber: rhfErrors.phoneNumber?.message,
    emailId: rhfErrors.emailId?.message,
    degree: rhfErrors.degree?.message,
    program: rhfErrors.program?.message,
    isTermsAccepted: rhfErrors.isTermsAccepted?.message,
  };

  return {
    control,
    errors,
    isSubmitting: isLoading,
    isOTPLoading,
    programsList,
    showProgramField,
    isProgram,
    isProgramListPage,
    isAccaPage,
    isMobile,
    isSpecificPage: props.isSpecificPage,
    isFaq: props.isFaq,
    isSeatsFilled: props.isSeatsFilled,
    seatsFilled: props.seatsFilled,
    landingPageValue,
    showStatsCards: props.showStatsCards,
    countries: ALL_COUNTRIES,
    handleFieldChange,
    handleDegreeChange,
    handleProgramChange,
    handleFieldBlur,
    updateCountryCode,
    handleFormSubmit,
    errorAccordingToFieldName,
    degreeOptions,
    SELECT_DEGREE,
    SELECT_PROGRAM,
  };
}
